import { useEffect, useRef } from "react";
import {
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { initFirebase } from "@shared/services/api/firebase";

export type Args = {
  setTokenExpiredStatus: (isTokenExpired: boolean) => void;
};

export function useSyncFirebaseToken({ setTokenExpiredStatus }: Args) {
  const lastUserIdRef = useRef<string | null>(null);
  const lastCheckRef = useRef<number>(0);

  useEffect(() => {
    const MIN_INTERVAL = 3000;

    const setup = async () => {
      try {
        const auth = await initFirebase();
        await setPersistence(auth, browserLocalPersistence);

        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
          const now = Date.now();

          if (now - lastCheckRef.current < MIN_INTERVAL) {
            const prev = lastUserIdRef.current;
            if (currentUser?.uid === prev) return;
          }

          lastCheckRef.current = now;
          lastUserIdRef.current = currentUser?.uid ?? null;

          if (currentUser) {
            try {
              const tokenResult = await currentUser.getIdTokenResult();
              const isExpired =
                Date.now() >= new Date(tokenResult.expirationTime).getTime();

              setTokenExpiredStatus(isExpired);
            } catch (err) {
              console.error("Error checking token expiration:", err);
              setTokenExpiredStatus(true);
            }
          } else {
            setTokenExpiredStatus(true);
          }
        });

        return unsubscribe;
      } catch (err) {
        console.error("Error setting up token expiration listener:", err);
        setTokenExpiredStatus(true);
      }
    };

    setup();
  }, [setTokenExpiredStatus]);
}

export default useSyncFirebaseToken;
